import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import request from "../utils/request";
import { buildNextPath } from "../utils/authRedirect";

const deliveryTimeOptions = [
  "8:00 AM",
  "8:30 AM",
  "9:00 AM",
  "9:30 AM",
  "10:00 AM",
  "10:30 AM",
  "11:00 AM",
  "11:30 AM",
  "12:00 PM",
  "12:30 PM",
  "1:00 PM",
  "1:30 PM",
  "2:00 PM",
  "2:30 PM",
  "3:00 PM",
  "3:30 PM",
  "4:00 PM",
];

const ENTRY_TOKEN_STORAGE_PREFIX = "entry_instructions_token:";

function OrderDetailsScreen() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [entryInstructions, setEntryInstructions] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [entryToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const storageKey = `${ENTRY_TOKEN_STORAGE_PREFIX}${window.location.pathname}`;
    const urlToken = (params.get("token") || "").trim();

    if (urlToken) {
      sessionStorage.setItem(storageKey, urlToken);
      return urlToken;
    }

    return (sessionStorage.getItem(storageKey) || "").trim();
  });

  const requestConfig = useMemo(
    () =>
      entryToken
        ? {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${entryToken}`,
            },
          }
        : { withCredentials: true },
    [entryToken]
  );

  useEffect(() => {
    const nextPath = buildNextPath(location);
    const currentPath = `${location.pathname}${location.search}${location.hash}`;

    if (currentPath !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await request.get(`/orders/${orderId}/entry-instructions`, requestConfig);

        if (!response.data.success) {
          setError(response.data.message || "Failed to load order details.");
          return;
        }

        setOrderData(response.data);
        setEntryInstructions(response.data.order?.entryInstructions || "");
        setDeliveryTime(response.data.order?.hostSetDeliveryTime || "");
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError(err.response?.data?.message || "Failed to load order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, requestConfig]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!entryInstructions.trim()) {
      setError("Entry instructions are required.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccessMessage("");

      const response = await request.patch(
        `/orders/${orderId}/entry-instructions`,
        {
          entryInstructions: entryInstructions.trim(),
          deliveryTime,
        },
        requestConfig
      );

      if (!response.data.success) {
        setError(response.data.message || "Failed to save entry instructions.");
        return;
      }

      setOrderData((current) => ({
        ...(current || {}),
        canEdit: response.data.canEdit,
        order: response.data.order,
        property: response.data.property,
      }));
      setEntryInstructions(response.data.order?.entryInstructions || "");
      setDeliveryTime(response.data.order?.hostSetDeliveryTime || "");
      setSuccessMessage(response.data.message || "Entry instructions saved.");
    } catch (err) {
      console.error("Error submitting entry instructions:", err);
      if (err.response?.status === 409) {
        setOrderData((current) =>
          current ? { ...current, canEdit: false } : current
        );
      }
      setError(err.response?.data?.message || "Failed to save entry instructions.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const canEdit = orderData?.canEdit !== false;
  const order = orderData?.order;
  const property = orderData?.property;

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#FF4D4D]" />
          <p className="font-medium text-gray-700">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900">Unable to load this order</h2>
          <p className="mt-2 text-gray-600">{error || "Order details are unavailable."}</p>
          <div className="mt-6">
            <Link
              to="/order-list"
              className="inline-flex items-center rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-gray-800"
            >
              Back to Orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-full px-6 py-8"
      style={{
        background:
          "linear-gradient(180deg, #FFFFFF 0%, #FFF5F4 30%, #F8FAFC 60%, #EFF4FF 100%)",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#FF9900]">
              Host Access
            </p>
            <h1 className="mt-1 text-[30px] font-bold leading-tight text-gray-900">
              Entry Instructions
            </h1>
            <p className="mt-2 text-[15px] text-gray-700">
              Review and update access details for this order.
            </p>
          </div>
          <Link
            to="/order-list"
            className="inline-flex items-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-200 transition hover:bg-gray-50"
          >
            Back to Orders
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.05fr_1fr]">
          <div className="rounded-2xl border border-white/70 bg-white/95 p-8 shadow-xl">
            <h2 className="mb-4 text-[17px] font-semibold text-gray-900">Order Details</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <p className="text-[12px] uppercase tracking-wide text-gray-500">Order ID</p>
                <p className="text-[15px] font-semibold text-gray-900">#{order?.uniqueId ?? "—"}</p>
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wide text-gray-500">Status</p>
                <p className="text-[15px] font-semibold text-gray-900">{order?.status || "N/A"}</p>
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wide text-gray-500">Guest Email</p>
                <p className="break-all text-[15px] font-semibold text-gray-900">{order?.guestEmail || "N/A"}</p>
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wide text-gray-500">Delivery Date</p>
                <p className="text-[15px] font-semibold text-gray-900">{formatDate(order?.inStockDate)}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[12px] uppercase tracking-wide text-gray-500">Delivery Address</p>
                <p className="text-[15px] font-semibold leading-snug text-gray-900">
                  {property?.address?.line1}
                  {property?.address?.line2 ? <><br />{property.address.line2}</> : null}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/70 bg-white/95 p-8 shadow-xl">
            <h2 className="mb-4 text-[17px] font-semibold text-gray-900">Current Access Notes</h2>
            <p className="whitespace-pre-wrap text-[14px] leading-7 text-gray-700">
              {entryInstructions || "No entry instructions saved yet."}
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-white/70 bg-white/95 p-8 shadow-2xl"
        >
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-[17px] font-semibold text-gray-900">
                {canEdit ? "Update Entry Instructions" : "Entry Instructions Locked"}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {canEdit
                  ? "Changes save directly to the order and property access notes."
                  : "Completed and cancelled orders are read-only."}
              </p>
            </div>
            {!canEdit && (
              <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-gray-600">
                Read only
              </span>
            )}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          )}

          {!canEdit && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-700">
                This order can still be reviewed here, but edits are no longer allowed.
              </p>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label htmlFor="entryInstructions" className="mb-2 block text-[14px] font-semibold text-gray-800">
                Entry Instructions <span className="text-red-500">*</span>
              </label>
              <textarea
                id="entryInstructions"
                value={entryInstructions}
                onChange={(event) => {
                  setEntryInstructions(event.target.value);
                  if (successMessage) setSuccessMessage("");
                }}
                rows={6}
                required
                disabled={!canEdit || submitting}
                className="w-full resize-y rounded-xl border border-gray-200 bg-white px-4 py-3 focus:border-[#FF6B6B] focus:ring-2 focus:ring-[#FF6B6B] disabled:cursor-not-allowed disabled:bg-gray-100"
                placeholder="e.g., Keypad code is 1234, park in spot #7, enter through side door..."
              />
              <p className="mt-1 text-sm text-gray-500">
                This updates both the order notes and the saved property access description.
              </p>
            </div>

            <div>
              <label htmlFor="deliveryTime" className="mb-2 block text-[14px] font-semibold text-gray-800">
                Delivery Time (when house cleaned)
              </label>
              <select
                id="deliveryTime"
                value={deliveryTime}
                onChange={(event) => {
                  setDeliveryTime(event.target.value);
                  if (successMessage) setSuccessMessage("");
                }}
                disabled={!canEdit || submitting}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:border-[#FF6B6B] focus:ring-2 focus:ring-[#FF6B6B] disabled:cursor-not-allowed disabled:bg-gray-100"
              >
                <option value="">Select a time</option>
                {deliveryTimeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Leave this blank if Bellhopt should continue without a host-set delivery time.
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={submitting || !canEdit || !entryInstructions.trim()}
              className="rounded-full bg-gradient-to-r from-[#FF4D4D] to-[#FF6B6B] px-6 py-3 font-semibold text-white shadow-lg transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Save Entry Instructions"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrderDetailsScreen;
